import {Game} from "../Game";
import {TimelineSprite} from "@nolimitcity/slot-launcher/bin/animation/TimelineSprite";
import {Symbol, SymbolStates, XBombStates} from "../reels/Symbol";
import {gsap} from "gsap";
import {Sound} from "../sound/Sound";
import {MathHelper} from "@nolimitcity/slot-game/bin/utils/MathHelper";

export class OverlaysForSymbols extends PIXI.Container {

    private readonly _gEEffectItems: PIXI.spine.Spine[];

    constructor() {
        super();
        this.name = "OverlaysForSymbols";

        this._gEEffectItems = [];

        this.onGridExpansion();
    }

    // #76
    public playBzBreak(sym: Symbol): void {
        const item: TimelineSprite = new TimelineSprite(Game.assetLoader.getTextures(sym.name[1] + "Break"));

        item.blendMode = PIXI.BLEND_MODES.SCREEN;
        item.anchor.set(0.5);
        item.scale.set(Game.scaleFactor);
        item.position.set(sym.position.x, sym.position.y);
        this.addChild(item);
        item.getAnimationAutoShowHide(true, true, undefined, undefined, () => {
            if (item.parent) {
                item.parent.removeChild(item);
                item.destroy();
            }
        });
    }

    public playMultiplierLand(symbol: Symbol, toY: number): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline(),
            item: PIXI.spine.Spine = new PIXI.spine.Spine(Game.assetLoader.getSpineAsset("multiplier")),
            animName: string = "land_" + Game.getMultiplierLvl(symbol.aliasName),
            multiplierName: string = symbol.aliasName.toLowerCase(),
            multiplierLvl: string = Game.getMultiplierLvl(multiplierName);

        item.skeleton.setSkinByName((multiplierLvl + "/" + multiplierName));

        item.scale.set(Game.scaleFactor);
        item.x = symbol.x;
        item.y = toY;

        tl.add(() => {
            this.addChild(item);
            symbol.visible = false;
        });
        tl.add(Game.createTimelineForAnimation(item, 0, animName));
        tl.add(() => {
            symbol.visible = true;
            if (item.parent) {
                item.parent.removeChild(item);

                item.destroy();
            }
        });

        return tl;
    }

    public playMultiplierPick(symbol: Symbol): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline(),
            item: PIXI.spine.Spine = new PIXI.spine.Spine(Game.assetLoader.getSpineAsset("multiplier")),
            animName: string = "pick_" + Game.getMultiplierLvl(symbol.aliasName),
            multiplierName: string = symbol.aliasName.toLowerCase(),
            multiplierLvl: string = Game.getMultiplierLvl(multiplierName),
            multiplierVal: number = +symbol.aliasName.substring(1),
            mShimmerSoundKey: string = "crateShimmer" + MathHelper.randomIntInRange(1, 3);

        item.skeleton.setSkinByName((multiplierLvl + "/" + multiplierName));
        item.scale.set(Game.scaleFactor);
        item.position = symbol.position;

        if (multiplierVal >= 2 && multiplierVal < 10) {
            tl.add(() => {
                Game.soundPlayer.playSound(Sound.CRATE_PICK_M2_M8, 1);
            });
        } else if (multiplierVal >= 10 && multiplierVal < 100) {
            tl.add(() => {
                Game.soundPlayer.playSound(Sound.CRATE_PICK_M10_M50, 1);
            });
        } else if (multiplierVal >= 100 && multiplierVal <= 500) {
            tl.add(() => {
                Game.soundPlayer.playSound(Sound.CRATE_PICK_M100_M500, 1);
            });
        } else if (multiplierVal <= 1000) {
            tl.add(() => {
                Game.soundPlayer.playSound(Sound.CRATE_PICK_M1000, 1);
            });
        } else {
            tl.add(() => {
                Game.soundPlayer.playSound(Sound.CRATE_PICK_M2000, 1);
            });
        }

        tl.add(() => {
            Game.soundPlayer.playSound(mShimmerSoundKey, 1);
        }, "<")

        tl.add(() => {
            Game.soundPlayer.playSound(Sound.TANK_DRIVE_LOOP, 1, Number.MAX_VALUE);

            this.addChild(item);
            symbol.visible = false;
        });
        tl.add(Game.createTimelineForAnimation(item, 0, animName));
        tl.add(() => {
            if (item.parent) {
                item.parent.removeChild(item);

                item.destroy();
            }
        });

        return tl;
    }

    // #266
    public playLandForTankBoosters(symbol: Symbol, toY: number): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline(),
            item: PIXI.spine.Spine = new PIXI.spine.Spine(Game.assetLoader.getSpineAsset(symbol.name));

        item.scale.set(Game.scaleFactor);
        item.x = symbol.x;
        item.y = toY;

        tl.add(() => {
            this.addChild(item);
        });
        tl.add(Game.createTimelineForAnimation(item, 0, SymbolStates.LAND));
        tl.add(() => {
            if (item.parent) {
                item.parent.removeChild(item);

                item.destroy();
            }
        });

        return tl;
    }

    public playEffectAnimationGE(symbol: Symbol, animName: string): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline(),
            item: PIXI.spine.Spine = new PIXI.spine.Spine(Game.assetLoader.getSpineAsset("gEEffect"));

        item.scale.set(Game.scaleFactor);
        item.position = symbol.position;

        tl.add(() => {
            this.addChild(item);
        });
        tl.add(Game.createTimelineForAnimation(item, 0, animName));
        tl.add(() => {
            if (item.parent) {
                item.parent.removeChild(item);

                item.destroy();

                this._gEEffectItems.pop();
            }
        });

        this._gEEffectItems.push(item);

        return tl;
    }

    public handleGridExpansionEffectOnGE(duration: number, xOffset: number, yOffset: number): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline();

        if (this._gEEffectItems.length) {
            this._gEEffectItems.forEach((item: PIXI.spine.Spine) => {
                tl.to(item, {
                    duration,
                    x: (item.x - xOffset),
                    y: (item.y - yOffset)
                }, 0);
            });
        }

        return tl;
    }

    public onGridExpansion(): void {
        const [xOffset, yOffset] = this.getGridContainerOffset(Game.gridSize);

        this.position.set(Game.REEL_AREA_X - xOffset, Game.REEL_AREA_Y - yOffset);
    }

    // #132, #105
    private getGridContainerOffset(gridName: string): number[] {
        switch (gridName) {
            case "8by6":
                return [38, -45];
            case "6by8":
                return [-47, 55];
            case "8by8":
                return [38, 55];
            case "9by8":
                return [34.3, 20];
            case "8by9":
                return [0, 60];
            case "9by9":
                return [39, 60];
            default:
                return [3, 0];
        }
    }
}